import React, { useState, useEffect } from "react";
import { Row, Col, FormGroup, Label, Input, Button, Table } from "reactstrap";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  getDriverDeductionByAll,
  createDeductionType,
  saveBulkDriverDeductions
} from "../../../Services/Driver.service";
import { showAlert } from "../../../Services/Shared.service";
import TrashIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import { Spinner } from "../../../AbstractElements";

import {
  VALUE_TYPE_OPTIONS,
  CALC_TYPE_OPTIONS,
  BOOKING_TYPE_OPTIONS
} from "./DeductionConstants";

const BulkDeductionForm = ({ driverScheme, onClose, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [existingTypes, setExistingTypes] = useState([]);
  const [focusedRowIndex, setFocusedRowIndex] = useState(null);

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      rows: [
        {
          deductionType: null,
          valueType: VALUE_TYPE_OPTIONS[0],
          value: "",
          calculationType: CALC_TYPE_OPTIONS[0],
          bookingType: BOOKING_TYPE_OPTIONS.find(o => o.value === "All"),
        }
      ]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "rows"
  });

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      const resp = await getDriverDeductionByAll();
      const dataArr = resp.data?.searchResponse || [];
      const types = dataArr.map((t) => ({
        label: t.deductionType,
        value: t.id,
        calculateOn: t.calculateOn,
      }));
      setExistingTypes(types);
    } catch (e) {
      console.error("Error fetching types", e);
    }
  };

  const handleAddRow = () => {
    append({
      deductionType: null,
      valueType: VALUE_TYPE_OPTIONS[0],
      value: "",
      calculationType: CALC_TYPE_OPTIONS[0],
      bookingType: BOOKING_TYPE_OPTIONS.find(o => o.value === "All"),
    });
  };

  const handleRemoveRow = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const onSubmit = async (data) => {
    // 1. Check for duplicates within the current rows (Client-side)
    const combinations = new Set();
    for (const row of data.rows) {
      const comboKey = `${row.deductionType.value}-${row.bookingType.value}`;
      if (combinations.has(comboKey)) {
        showAlert("error", `Duplicate entry found for ${row.deductionType.label} with booking type ${row.bookingType.label}`);
        return;
      }
      combinations.add(comboKey);
    }

    setLoading(true);
    try {
      const deductionList = [];
      for (const row of data.rows) {
        let typeId = row.deductionType.value;

        if (row.deductionType.__isNew__) {
          const typeResp = await createDeductionType({
            deductionType: row.deductionType.label,
            deductionDisplayName: row.deductionType.label,
            calculateOn: row.calculationType.value
          });
          typeId = typeResp.data.id;
        }

        deductionList.push({
          driverSchemeId: driverScheme.id,
          serviceCityId: driverScheme.serviceCityId,
          driverDeductionId: typeId,
          valueType: row.valueType.value,
          value: parseFloat(row.value),
          calculateType: row.calculationType.value,
          bookingTypeDeduction: row.bookingType.value,
        });
      }

      await saveBulkDriverDeductions(deductionList);

      showAlert("success", "All deductions added successfully!");
      onRefresh();
      onClose();
    } catch (error) {
      console.error("Error saving deductions:", error);
      const msg =
        error.response?.data?.message ||
        (typeof error.response?.data === "string" ? error.response.data : null) ||
        error.message ||
        "Failed to save some deductions.";
      showAlert("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "8px",
      fontSize: "13px",
      minHeight: "38px",
      backgroundColor: state.isDisabled ? "#e9ecef" : "white",
      color: state.isDisabled ? "#6c757d" : "#333",
      borderColor: state.isFocused ? "#39B54A" : "#dee2e6",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(57, 181, 74, 0.25)" : "none",
      cursor: state.isDisabled ? "not-allowed" : "default",
      "&:hover": {
        borderColor: state.isFocused ? "#39B54A" : (state.isDisabled ? "#dee2e6" : "#ced4da")
      }
    }),
    singleValue: (base, state) => ({
      ...base,
      color: state.isDisabled ? "#6c757d" : "#333"
    }),
    placeholder: (base) => ({
      ...base,
      color: "#999"
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#39B54A"
        : state.isFocused
          ? "#E3F5E1"
          : "white",
      color: state.isSelected ? "white" : "#333",
      fontSize: "13px",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#39B54A",
        color: "white"
      }
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 })
  };

  return (
    <div className="p-1">
      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table borderless style={{ minWidth: "900px", overflow: "visible" }}>
          <thead>
            <tr className="text-muted small text-uppercase">
              <th style={{ width: "25%" }}>Deduction Type</th>
              <th style={{ width: "18%" }}>Value Type</th>
              <th style={{ width: "12%" }}>Value</th>
              <th style={{ width: "18%" }}>Calc Type</th>
              <th style={{ width: "22%" }}>Booking Type</th>
              <th style={{ width: "5%" }}></th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => {
              const watchedDeductionType = watch(`rows.${index}.deductionType`);
              return (
                <tr key={field.id}>
                  <td>
                    <Controller
                      name={`rows.${index}.deductionType`}
                      control={control}
                      rules={{ required: "Required" }}
                      render={({ field: selectField, fieldState: { error } }) => (
                        <div>
                          <CreatableSelect
                            {...selectField}
                            isClearable
                            options={existingTypes}
                            isValidNewOption={(inputValue) => inputValue.trim().length > 0}
                            placeholder="Select/Create..."
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            onChange={(val) => {
                              selectField.onChange(val);
                              if (val && !val.__isNew__ && val.calculateOn) {
                                const matchedCalc = CALC_TYPE_OPTIONS.find(o => o.value === val.calculateOn);
                                if (matchedCalc) {
                                  setValue(`rows.${index}.calculationType`, matchedCalc);
                                }
                              } else if (!val) {
                                setValue(`rows.${index}.calculationType`, CALC_TYPE_OPTIONS[0]);
                              }
                            }}
                          />
                          {error && <span className="text-danger small mt-1 d-block">{error.message}</span>}
                        </div>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name={`rows.${index}.valueType`}
                      control={control}
                      rules={{ required: "Required" }}
                      render={({ field: selectField, fieldState: { error } }) => (
                        <div>
                          <Select
                            {...selectField}
                            options={VALUE_TYPE_OPTIONS}
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                          />
                          {error && <span className="text-danger small mt-1 d-block">{error.message}</span>}
                        </div>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name={`rows.${index}.value`}
                      control={control}
                      rules={{
                        required: "Required",
                        validate: (val) => {
                          if (parseFloat(val) < 0) {
                            return "Cannot be less than 0";
                          }
                          const valType = watch(`rows.${index}.valueType`)?.value;
                          if (valType === "P" && parseFloat(val) > 100) {
                            return "Cannot exceed 100%";
                          }
                          return true;
                        }
                      }}
                      render={({ field: inputField, fieldState: { error } }) => (
                        <div>
                          <Input
                            {...inputField}
                            type="number"
                            placeholder="0.00"
                            onFocus={() => setFocusedRowIndex(index)}
                            onBlur={() => {
                              inputField.onBlur();
                              setFocusedRowIndex(null);
                            }}
                            className="form-control"
                            style={{
                              borderRadius: "8px",
                              fontSize: "13px",
                              height: "38px",
                              backgroundColor: "white",
                              color: "#333",
                              borderColor: error ? "#dc3545" : (focusedRowIndex === index ? "#39B54A" : "#dee2e6"),
                              boxShadow: error ? "0 0 0 0.2rem rgba(220, 53, 69, 0.25)" : (focusedRowIndex === index ? "0 0 0 0.2rem rgba(57, 181, 74, 0.25)" : "none"),
                              outline: "none"
                            }}
                          />
                          {error && <span className="text-danger small mt-1 d-block">{error.message}</span>}
                        </div>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name={`rows.${index}.calculationType`}
                      control={control}
                      rules={{ required: "Required" }}
                      render={({ field: selectField, fieldState: { error } }) => (
                        <div>
                          <Select
                            {...selectField}
                            options={CALC_TYPE_OPTIONS}
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            isDisabled={watchedDeductionType && !watchedDeductionType.__isNew__}
                          />
                          {error && <span className="text-danger small mt-1 d-block">{error.message}</span>}
                        </div>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name={`rows.${index}.bookingType`}
                      control={control}
                      rules={{ required: "Required" }}
                      render={({ field: selectField, fieldState: { error } }) => (
                        <div>
                          <Select
                            {...selectField}
                            options={BOOKING_TYPE_OPTIONS}
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                          />
                          {error && <span className="text-danger small mt-1 d-block">{error.message}</span>}
                        </div>
                      )}
                    />
                  </td>
                  <td className="text-center align-middle">
                    <Button
                      color="link"
                      className="text-danger p-0"
                      onClick={() => handleRemoveRow(index)}
                      disabled={fields.length === 1}
                    >
                      <TrashIcon />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between mt-4">
        <Button
          color="light"
          style={{
            borderRadius: "10px",
            fontWeight: "600",
            border: "1px solid #ddd",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "8px 20px"
          }}
          onClick={handleAddRow}
        >
          <AddIcon fontSize="small" style={{ marginRight: "8px" }} />
          Add More
        </Button>

        <Button
          color="primary"
          style={{
            borderRadius: "10px",
            fontWeight: "600",
            padding: "10px 30px",
            backgroundColor: "#39B54A",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
          onClick={handleSubmit(onSubmit)}
          disabled={loading}
        >
          {loading ? (
            <Spinner size="sm" style={{ marginRight: "8px" }} />
          ) : (
            <SaveIcon fontSize="small" style={{ marginRight: "8px" }} />
          )}
          Save All Deductions
        </Button>
      </div>
    </div>
  );
};

export default BulkDeductionForm;
